import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Collapse, Typography } from "@mui/material";
import { ArrowRightLeft, Lock } from "lucide-react";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseBadge from "@reusables/BaseBadge";
import BaseTooltip from "@reusables/BaseTooltip";
import { useGetModuleCurrenciesQuery } from "@redux/features/currencies/currenciesApi";
import { SaleOrdersFormTyping } from "../../types";
import { useBaseCurrency, useOrderCurrency } from "../../../OrderCurrencyContext";
import { convertLinePrices } from "../../../../orderCurrency";

/**
 * Currency of the order. The exchange rate is managed by the system, so it is only shown below
 * the dropdown, never edited.
 */
export default function CurrencySection() {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.dropdowns.currency" });

    const { control, getValues, setValue } = useFormContext<SaleOrdersFormTyping>();

    const mode = useWatch({ control, name: "mode" });

    const { data: currencyOptions = [], isLoading: isCurrencyOptionsLoading } =
        useGetModuleCurrenciesQuery("sale");

    const baseCurrency = useBaseCurrency();
    const { currency, rate, isRateLocked } = useOrderCurrency();

    return (
        <div>
            <Controller
                name="currency"
                control={control}
                render={({ field, fieldState }) => (
                    <BaseDropdown
                        {...field}
                        {...fieldState}
                        onChange={(nextCurrency) => {
                            if (!nextCurrency) return;

                            // Lines priced before the switch keep their value in the new currency
                            const lines = getValues("lines");
                            if (lines.length) {
                                setValue(
                                    "lines",
                                    convertLinePrices(lines, field.value, nextCurrency)
                                );
                            }

                            field.onChange(nextCurrency);
                        }}
                        label={t("label")}
                        placeholder={t("placeholder")}
                        options={currencyOptions}
                        getter={{
                            label: (opt) => opt.name,
                            key: (opt) => opt.id,
                            caption: (opt) => opt.code,
                            renderOption: (opt, icon) => (
                                <div className="flex justify-between">
                                    <div className="flex items-center space-x-2">
                                        <div>
                                            {opt.name} ({opt.symbol})
                                        </div>
                                        {opt.isBase && <BaseBadge>{t("base")}</BaseBadge>}
                                    </div>
                                    {icon}
                                </div>
                            ),
                        }}
                        autocomplete
                        isLoading={isCurrencyOptionsLoading}
                        // The backend refuses to change the currency of a saved order
                        disabled={mode === "editing"}
                        disableClearable
                    />
                )}
            />

            <Collapse in={!currency.isBase}>
                <div className="mt-2">
                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ opacity: 0.5 }}
                        className="flex items-center space-x-2"
                    >
                        <ArrowRightLeft size={12} />
                        <span>
                            {t("rate", {
                                base: baseCurrency.code,
                                rate,
                                currency: currency.code,
                            })}
                        </span>
                        {isRateLocked && (
                            <BaseTooltip title={t("locked") + ""} placement="top">
                                <Lock size={12} />
                            </BaseTooltip>
                        )}
                    </Typography>
                </div>
            </Collapse>
        </div>
    );
}
